import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useSystemBranding, ShieldLogo, BrandTitle } from '../../context/SystemBrandingContext';
import type { SystemBrandingConfig } from '../../types';
import { 
  Save, 
  Check, 
  RefreshCw, 
  Folder,
  Eye,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Palette,
  Image as ImageIcon,
  Download,
  FileSpreadsheet,
  Globe,
  Copy
} from 'lucide-react';

export const BrandingSettingsView: React.FC = () => {
  const { user, tenant, role, isImpersonating } = useAuth();
  const baseRole = (user?.role || role || '') as string;
  const userRole = ['superadmin', 'tenant_admin', 'org_admin'].includes(baseRole)
    ? baseRole
    : ((user?.granular_role || baseRole) as string);
  const isSuperadmin = (userRole === 'superadmin' || userRole === 'tenant_admin') && !isImpersonating;
  const { branding: systemBranding, updateBranding: saveSystemBranding } = useSystemBranding();

  const [activeTab, setActiveTab] = useState<'system' | 'tenant'>(isSuperadmin ? 'system' : 'tenant');

  useEffect(() => {
    if (!isSuperadmin) {
      setActiveTab('tenant');
    }
  }, [isSuperadmin]);

  const [sysUploadingLogo, setSysUploadingLogo] = useState(false);
  const [tenantUploadingLogo, setTenantUploadingLogo] = useState(false);

  const handleSysLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSysUploadingLogo(true);
    try {
      const res = await ApiClient.uploadLogo(file, 'login_logo_url');
      if (res.ok && res.url) {
        setSysLogoUrl(res.url);
        setSysShieldType('custom');
      } else {
        alert(res.error || 'Hiba a logó feltöltése során');
      }
    } catch (err: any) {
      alert(err.message || 'Hiba a feltöltés során');
    } finally {
      setSysUploadingLogo(false);
    }
  };

  const handleTenantLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTenantUploadingLogo(true);
    try {
      const res = await ApiClient.uploadLogo(file, 'logo_url');
      if (res.ok && res.url) {
        setLogoUrl(res.url);
      } else {
        alert(res.error || 'Hiba a szervezeti logó feltöltése során');
      }
    } catch (err: any) {
      alert(err.message || 'Hiba a feltöltés során');
    } finally {
      setTenantUploadingLogo(false);
    }
  };

  // ── RENDSZERSZINTŰ BRANDING ÁLLAPOT ─────────────────────────────
  const [sysTitlePrefix, setSysTitlePrefix] = useState('Közzé');
  const [sysTitleMiddle, setSysTitleMiddle] = useState('Tér');
  const [sysTitleSuffix, setSysTitleSuffix] = useState('Pro.');
  const [sysSubtitle, setSysSubtitle] = useState('2011. évi CXII. törvény szerinti közzétételi platform');
  const [sysVersion, setSysVersion] = useState('v4.5');
  const [sysShieldType, setSysShieldType] = useState<'shield' | 'shield_check' | 'shield_alert' | 'custom'>('shield');
  const [sysShieldColor, setSysShieldColor] = useState<'green' | 'indigo' | 'blue' | 'slate'>('green');
  const [sysLogoUrl, setSysLogoUrl] = useState('');
  const [sysPublicPortalTimeout, setSysPublicPortalTimeout] = useState<number>(30);
  const [sysTotpIssuer, setSysTotpIssuer] = useState('KözzéTér Pro');
  const [sysSaving, setSysSaving] = useState(false);
  const [sysSavedSuccess, setSysSavedSuccess] = useState(false);

  useEffect(() => {
    if (systemBranding) {
      setSysTitlePrefix(systemBranding.title_prefix ?? 'Közzé');
      setSysTitleMiddle(systemBranding.title_middle !== undefined ? systemBranding.title_middle : (systemBranding.title_prefix === 'Köz' ? '' : 'Tér'));
      setSysTitleSuffix(systemBranding.title_suffix ?? (systemBranding.title_prefix === 'Köz' ? 'adat' : 'Pro.'));
      setSysSubtitle(systemBranding.subtitle || '2011. évi CXII. törvény szerinti közzétételi platform');
      setSysVersion(systemBranding.version || 'v4.5');
      setSysShieldType(systemBranding.shield_type || 'shield');
      setSysShieldColor(systemBranding.shield_color || 'green');
      setSysLogoUrl(systemBranding.logo_url || '');
      setSysPublicPortalTimeout(systemBranding.public_portal_timeout || 30);
      setSysTotpIssuer(systemBranding.totp_issuer || 'KözzéTér Pro');
    }
  }, [systemBranding]);

  const handleSystemSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSysSaving(true);
    setSysSavedSuccess(false);

    const prefix = sysTitlePrefix.trim();
    const middle = sysTitleMiddle.trim();
    const suffix = sysTitleSuffix.trim();
    const systemTitle = middle ? `${prefix}${middle} ${suffix}` : `${prefix}${suffix}`;

    const payload: Partial<SystemBrandingConfig> = {
      title_prefix: prefix,
      title_middle: middle,
      title_suffix: suffix,
      system_title: systemTitle,
      subtitle: sysSubtitle.trim(),
      version: sysVersion.trim(),
      shield_type: sysShieldType,
      shield_color: sysShieldColor,
      logo_url: sysLogoUrl.trim(),
      prefix_color: '#16a34a',
      middle_color: '#0f172a',
      suffix_color: '#16a34a',
      public_portal_timeout: Number(sysPublicPortalTimeout) > 0 ? Number(sysPublicPortalTimeout) : 30,
      totp_issuer: sysTotpIssuer.trim() || 'KözzéTér Pro',
    };

    const res = await saveSystemBranding(payload);
    if (res.ok) {
      setSysSavedSuccess(true);
      setTimeout(() => setSysSavedSuccess(false), 3500);
    } else {
      alert(res.error || 'Hiba a rendszerszintű arculat mentése során');
    }
    setSysSaving(false);
  };

  // ── SZERVEZETI BRANDING ÁLLAPOT ─────────────────────────────────
  const [name, setName] = useState('Gyál Város Polgármesteri Hivatal');
  const [contactsText, setContactsText] = useState(
    'CÍM: 2360 Gyál, Kőrösi út 112-114.\nTEL.: +36 (29) 540-930\nE-MAIL: hivatal@gyal.hu'
  );
  const [headerLayout, setHeaderLayout] = useState<'contact_left' | 'name_left' | 'center'>('contact_left');
  const [footerLeft, setFooterLeft] = useState('Bizalmas - csak belső használatra');
  const [footerCenter, setFooterCenter] = useState('Gyál Város Polgármesteri Hivatal');
  const [footerRight, setFooterRight] = useState('{page} / {pages} oldal');
  const [brandColor, setBrandColor] = useState('#1a5fd4');
  const [brandColor2, setBrandColor2] = useState('#007a52');
  const [logoUrl, setLogoUrl] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // ── KÖZZÉTÉTELI MÓD ÁLLAPOT ─────────────────────────────────────
  const [publishMode, setPublishMode] = useState<'local' | 'hybrid' | 'cloud'>('cloud');
  const [savingPublishMode, setSavingPublishMode] = useState(false);
  const [publishModeSaved, setPublishModeSaved] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    const loadBranding = async () => {
      setLoading(true);
      const res = await ApiClient.getBranding();
      if (res.ok && res.data) {
        const d = res.data;
        if (d.name) setName(d.name);
        if (d.brand_color) setBrandColor(d.brand_color);
        if (d.brand_color2) setBrandColor2(d.brand_color2);
        if (d.logo_url) setLogoUrl(d.logo_url);
        if (d.header_left) setContactsText(d.header_left);
        if (d.header_layout) setHeaderLayout(d.header_layout as any);
        if (d.footer_left !== undefined) setFooterLeft(d.footer_left);
        if (d.footer_center !== undefined) setFooterCenter(d.footer_center || d.name || '');
        if (d.footer_right !== undefined) setFooterRight(d.footer_right || '{page} / {pages} oldal');
        if (d.publish_mode) setPublishMode(d.publish_mode as any);
      } else if (tenant) {
        setName(tenant.name || '');
        setFooterCenter(tenant.name || '');
        if (tenant.publish_mode) setPublishMode(tenant.publish_mode as any);
      }
      setLoading(false);
    };
    loadBranding();
  }, [tenant]);

  const handleTenantSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSavedSuccess(false);

    const payload = {
      name,
      brand_color: brandColor,
      brand_color2: brandColor2,
      logo_url: logoUrl,
      header_left: contactsText,
      header_layout: headerLayout,
      footer_left: footerLeft,
      footer_center: footerCenter,
      footer_right: footerRight,
      publish_mode: publishMode,
    };

    const targetId = tenant?.id || 1;
    const res = await ApiClient.updateTenant(targetId, payload);
    if (res.ok) {
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3500);
    } else {
      alert(res.error || 'Hiba a branding mentése során');
    }
    setSaving(false);
  };

  const savePublishMode = async () => {
    setSavingPublishMode(true);
    setPublishModeSaved(false);
    const targetId = tenant?.id || 1;
    const res = await ApiClient.updateTenant(targetId, { publish_mode: publishMode });
    if (res.ok) {
      setPublishModeSaved(true);
      setTimeout(() => setPublishModeSaved(false), 3500);
    } else {
      alert(res.error || 'Hiba a közzétételi mód mentése során');
    }
    setSavingPublishMode(false);
  };

  const downloadStaticSite = async () => {
    try {
      const token = ApiClient.getToken();
      const res = await fetch('/api/export/static-site', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error('Statikus export hiba');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenant?.slug || 'szervezet'}_statikus_oldal.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e.message || 'Hiba a statikus exportálás során');
    }
  };

  const downloadFolderTemplate = async () => {
    try {
      await ApiClient.downloadFolderTemplateZip({ blank: false });
    } catch (e: any) {
      alert(e.message || 'Hiba a mappasablon letöltése során');
    }
  };

  const downloadKozadatCsv = async () => {
    try {
      const res = await ApiClient.downloadKozadatCsv();
      const skipMsg = res.skipped_no_attachment > 0 ? ` (${res.skipped_no_attachment} pont kimaradt melléklet nélkül)` : '';
      alert(`✅ ${res.row_count} sor sikeresen kiexportálva a közadat.hu import formátumba${skipMsg}!`);
    } catch (e: any) {
      alert(e.message || 'Hiba a Közadat CSV exportálása során');
    }
  };

  // Ideiglenes konfiguráció az élő előnézethez
  const previewConfig: Partial<SystemBrandingConfig> = {
    title_prefix: sysTitlePrefix,
    title_middle: sysTitleMiddle,
    title_suffix: sysTitleSuffix,
    system_title: sysTitleMiddle ? `${sysTitlePrefix}${sysTitleMiddle} ${sysTitleSuffix}` : `${sysTitlePrefix}${sysTitleSuffix}`,
    subtitle: sysSubtitle,
    version: sysVersion,
    shield_type: sysShieldType,
    shield_color: sysShieldColor,
    logo_url: sysLogoUrl,
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Top Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Palette className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            <span>{isSuperadmin ? 'Megjelenés & Arculat' : 'Szervezeti Megjelenés & Arculat'}</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            {isSuperadmin
              ? 'Rendszernév, belépési felület, pajzs logó és szervezeti fejléc testreszabása'
              : `Szervezeti fejléc, hivatalos logó, elérhetőségek és exportálási arculat (${tenant?.name || 'Saját intézmény'})`}
          </p>
        </div>

        {/* Tab gombok - Kizárólag Szuperadmin számára érhető el a Rendszerszintű fül */}
        {isSuperadmin ? (
          <div className="inline-flex p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
            <button
              type="button"
              onClick={() => setActiveTab('system')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === 'system'
                  ? 'bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Shield className="w-3.5 h-3.5" />
              <span>Rendszer & Belépés</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tenant')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === 'tenant'
                  ? 'bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Folder className="w-3.5 h-3.5" />
              <span>Szervezeti Fejléc & Export</span>
            </button>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-xl text-xs font-semibold text-blue-700 dark:text-blue-300">
            <Folder className="w-3.5 h-3.5" />
            <span>Szervezeti Fejléc & Export</span>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* 1. TAB: RENDSZERSZINTŰ MEGJELENÉS & BELÉPÉS (CSAK SUPERADMIN)  */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {isSuperadmin && activeTab === 'system' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8 shadow-xs">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-slate-100 dark:border-slate-800 mb-6">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>🛡️</span>
                <span>Rendszer Cím, Pajzs Logó & Belépési Arculat</span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Ezek a beállítások a teljes rendszer nevét, a felső menüsávot és a bejelentkezési képernyőt vezérlik.
              </p>
            </div>

            {sysSavedSuccess && (
              <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 rounded-lg text-xs font-bold border border-emerald-300 dark:border-emerald-500/30">
                <Check className="w-3.5 h-3.5" />
                <span>Rendszer arculat mentve!</span>
              </div>
            )}
          </div>

          <form onSubmit={handleSystemSave} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Bal oldal: Űrlap */}
              <div className="lg:col-span-7 space-y-6">
                {/* 1. Rendszernév három részletben (Zöld – Fekete/Fehér – Zöld) */}
                <div>
                  <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Rendszernév & Színkiemelés (Zöld – Fekete/Fehér – Zöld)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold block mb-1">
                        1. Zöld előtag:
                      </span>
                      <input
                        type="text"
                        required
                        value={sysTitlePrefix}
                        onChange={(e) => setSysTitlePrefix(e.target.value)}
                        placeholder="Közzé"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-emerald-300 dark:border-emerald-700 rounded-xl text-emerald-600 dark:text-emerald-400 font-extrabold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold block mb-1">
                        2. Fekete / Fehér közép:
                      </span>
                      <input
                        type="text"
                        value={sysTitleMiddle}
                        onChange={(e) => setSysTitleMiddle(e.target.value)}
                        placeholder="Tér"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold block mb-1">
                        3. Zöld utótag (Kiadás):
                      </span>
                      <input
                        type="text"
                        value={sysTitleSuffix}
                        onChange={(e) => setSysTitleSuffix(e.target.value)}
                        placeholder="Pro."
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-emerald-300 dark:border-emerald-700 rounded-xl text-emerald-600 dark:text-emerald-400 font-extrabold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                    A név összeállítva: <strong className="text-emerald-600 dark:text-emerald-400">{sysTitlePrefix}</strong><strong className="text-slate-900 dark:text-white">{sysTitleMiddle}</strong> <strong className="text-emerald-600 dark:text-emerald-400">{sysTitleSuffix}</strong>
                  </p>
                </div>

                {/* 2. Alcím & Verzió */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                      Alcím (Belépési oldal leírása)
                    </label>
                    <input
                      type="text"
                      value={sysSubtitle}
                      onChange={(e) => setSysSubtitle(e.target.value)}
                      placeholder="2011. évi CXII. törvény szerinti közzétételi platform"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                      Verziószám
                    </label>
                    <input
                      type="text"
                      value={sysVersion}
                      onChange={(e) => setSysVersion(e.target.value)}
                      placeholder="v4.0"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                      Publikus Portál Inaktivitási Időkorlát (mp)
                    </label>
                    <input
                      type="number"
                      min={5}
                      max={300}
                      value={sysPublicPortalTimeout}
                      onChange={(e) => setSysPublicPortalTimeout(Number(e.target.value) || 30)}
                      placeholder="30"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                      Bejelentkezett admin esetén ennyi másodperc inaktivitás (nincs egérmozgás/kattintás) után tér vissza a kezelőfelületre (alapértelmezett: 30 mp).
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                      Név a hitelesítő alkalmazásban (2FA)
                    </label>
                    <input
                      type="text"
                      maxLength={40}
                      value={sysTotpIssuer}
                      onChange={(e) => setSysTotpIssuer(e.target.value.replace(/:/g, ''))}
                      placeholder="KözzéTér Pro"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                      Így jelenik meg a fiók a Google / Microsoft Authenticatorban: <b>{(sysTotpIssuer.trim() || 'KözzéTér Pro')}</b> (felhasznalo@pelda.hu). Csak az ezután bekapcsolt 2FA-ra érvényes; a már beolvasott fiókot a telefonon át lehet nevezni. Ha egy alkalmazás az ékezeteket hibásan mutatja, írd ékezet nélkül (pl. KozzeTer Pro). Kettőspont nem használható.
                    </p>
                  </div>
                </div>

                {/* 3. Pajzs Logó Választó */}
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                  <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2.5">
                    Pajzs Logó Típusa (Tetszőlegesen cserélhető)
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <button
                      type="button"
                      onClick={() => setSysShieldType('shield')}
                      className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition cursor-pointer text-xs font-semibold ${
                        sysShieldType === 'shield'
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-500 dark:text-emerald-300 shadow-xs'
                          : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                      }`}
                    >
                      <Shield className="w-5 h-5 text-emerald-600" />
                      <span>Standard Pajzs</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSysShieldType('shield_check')}
                      className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition cursor-pointer text-xs font-semibold ${
                        sysShieldType === 'shield_check'
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-500 dark:text-emerald-300 shadow-xs'
                          : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                      }`}
                    >
                      <ShieldCheck className="w-5 h-5 text-emerald-600" />
                      <span>Hitelesített</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSysShieldType('shield_alert')}
                      className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition cursor-pointer text-xs font-semibold ${
                        sysShieldType === 'shield_alert'
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-500 dark:text-emerald-300 shadow-xs'
                          : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                      }`}
                    >
                      <ShieldAlert className="w-5 h-5 text-emerald-600" />
                      <span>Biztonsági</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSysShieldType('custom')}
                      className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition cursor-pointer text-xs font-semibold ${
                        sysShieldType === 'custom'
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-500 dark:text-emerald-300 shadow-xs'
                          : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                      }`}
                    >
                      <ImageIcon className="w-5 h-5 text-emerald-600" />
                      <span>Egyedi Kép</span>
                    </button>
                  </div>
                </div>

                {/* 4. Pajzs Színvilág */}
                {sysShieldType !== 'custom' && (
                  <div>
                    <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
                      Pajzs Színtéma
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                      {[
                        { id: 'green', label: 'Smaragdzöld (Közadat Zöld)', bg: 'bg-gradient-to-tr from-emerald-600 to-green-500' },
                        { id: 'indigo', label: 'Indigó', bg: 'bg-gradient-to-tr from-indigo-600 to-indigo-500' },
                        { id: 'blue', label: 'Királykék', bg: 'bg-gradient-to-tr from-blue-600 to-indigo-600' },
                        { id: 'slate', label: 'Sötét Grafit', bg: 'bg-gradient-to-tr from-slate-800 to-slate-700' },
                      ].map((th) => (
                        <button
                          key={th.id}
                          type="button"
                          onClick={() => setSysShieldColor(th.id as any)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium transition cursor-pointer ${
                            sysShieldColor === th.id
                              ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30 text-slate-900 dark:text-white font-bold ring-2 ring-emerald-500/20'
                              : 'border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                          }`}
                        >
                          <span className={`w-3.5 h-3.5 rounded-full ${th.bg}`} />
                          <span>{th.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Egyedi Kép Logó URL & Fájlfeltöltés */}
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-mono font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Egyedi Rendszer Logó / Kép (Feltöltés vagy URL)
                    </label>
                    {sysShieldType === 'custom' && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-bold">
                        Aktív logóként kiválasztva
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
                    <input
                      type="text"
                      value={sysLogoUrl}
                      onChange={(e) => {
                        setSysLogoUrl(e.target.value);
                        if (e.target.value) setSysShieldType('custom');
                      }}
                      placeholder="https://pelda.hu/logo.png vagy kattintson a feltöltésre..."
                      className="flex-1 px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />

                    <label className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-semibold cursor-pointer transition shrink-0">
                      {sysUploadingLogo ? (
                        <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-emerald-600" />
                      )}
                      <span>{sysUploadingLogo ? 'Feltöltés...' : '📁 Logó Feltöltése (R2)'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleSysLogoUpload}
                        disabled={sysUploadingLogo}
                      />
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    Feltöltött kép esetén automatikusan az egyedi logó jelenik meg a belépési oldalon és a felső menüben. Támogatott formátumok: PNG, SVG, JPG, WEBP.
                  </p>
                </div>

                {/* Mentés gomb */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={sysSaving}
                    className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/20 transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {sysSaving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>Rendszerszintű Arculat Mentése</span>
                  </button>
                </div>
              </div>

              {/* Jobb oldal: Élő Előnézeti Panel */}
              <div className="lg:col-span-5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-6">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  <Eye className="w-4 h-4 text-emerald-600" />
                  <span>Valós Idejű Élő Előnézet</span>
                </div>

                {/* Előnézet 1: Belépési Fejléc */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 text-center shadow-xs">
                  <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block mb-3">
                    [ Belépési Oldal Fejléce ]
                  </span>

                  <div className="mb-3 inline-block">
                    <ShieldLogo size="xl" overrideConfig={previewConfig} />
                  </div>

                  <div className="flex items-center justify-center gap-2">
                    <BrandTitle size="lg" overrideConfig={previewConfig} />
                    {sysVersion && (
                      <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/80">
                        {sysVersion}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 max-w-xs mx-auto">
                    {sysSubtitle || '2011. évi CXII. törvény szerinti közzétételi platform'}
                  </p>
                </div>

                {/* Előnézet 2: Navbar Brand */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 shadow-xs">
                  <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block mb-2">
                    [ Felső Menüsáv (Navbar) ]
                  </span>
                  <div className="flex items-center gap-3 p-2 bg-slate-100 dark:bg-[#0b0f1a] rounded-lg border border-slate-200 dark:border-slate-800">
                    <ShieldLogo size="md" overrideConfig={previewConfig} />
                    <div>
                      <div className="flex items-center gap-2">
                        <BrandTitle size="sm" overrideConfig={previewConfig} />
                        {sysVersion && (
                          <span className="text-[9px] px-1 py-0.2 rounded bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono border border-slate-200 dark:border-slate-700">
                            {sysVersion}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-500 truncate">
                        {tenant?.name || previewConfig.system_title || 'KözzéTér Pro.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* 2. TAB: SZERVEZETI FEJLÉC, LÁBLÉC & EXPORT ARCULAT             */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {(!isSuperadmin || activeTab === 'tenant') && (
        <>
          {loading ? (
            <div className="py-20 text-center text-slate-500 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
              Szervezeti branding adatok betöltése...
            </div>
          ) : (
            <div className="bg-[#eef4fc] dark:bg-slate-900/90 border border-blue-200/80 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-xs">
              <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-blue-200/70 dark:border-slate-800 mb-6">
                <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>🎨</span>
                  <span>Fejléc, lábléc, logó – {name || tenant?.name || 'Szervezet'}</span>
                </h2>

                {savedSuccess && (
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 rounded-lg text-xs font-bold border border-emerald-300 dark:border-emerald-500/30">
                    <Check className="w-3.5 h-3.5" />
                    <span>Branding sikeresen mentve!</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleTenantSave}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  {/* BAL OLDAL: ŰRLAP */}
                  <div className="lg:col-span-7 space-y-5">
                    {/* Szervezeti Logó */}
                    <div className="p-4 rounded-xl bg-white dark:bg-slate-950 border border-blue-200 dark:border-slate-800 space-y-2">
                      <label className="block text-[11px] font-mono font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Szervezeti Hivatalos Logó (Fejléc és Dokumentum Logó)
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
                        <input
                          type="text"
                          value={logoUrl}
                          onChange={(e) => setLogoUrl(e.target.value)}
                          placeholder="https://.../logo.png vagy kattintson a feltöltésre..."
                          className="flex-1 px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-blue-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <label className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:hover:bg-blue-900/60 dark:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-xl text-xs font-semibold cursor-pointer transition shrink-0">
                          {tenantUploadingLogo ? (
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                          ) : (
                            <ImageIcon className="w-4 h-4 text-blue-600" />
                          )}
                          <span>{tenantUploadingLogo ? 'Feltöltés...' : '📁 Logó Feltöltése (R2)'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleTenantLogoUpload}
                            disabled={tenantUploadingLogo}
                          />
                        </label>
                      </div>
                      {logoUrl && (
                        <div className="flex items-center gap-3 pt-1">
                          <img src={logoUrl} alt="Szervezeti logó" className="h-10 max-w-[120px] object-contain bg-slate-50 dark:bg-slate-900 p-1 rounded border border-slate-200 dark:border-slate-800" />
                          <span className="text-[10px] text-slate-500">Logó előnézet</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        Szervnév (Jobb/Bal layout szerint)
                      </label>
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-950 border border-blue-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        Elérhetőség (kulcs: érték soronként)
                      </label>
                      <textarea
                        rows={4}
                        value={contactsText}
                        onChange={(e) => setContactsText(e.target.value)}
                        className="w-full p-3 bg-white dark:bg-slate-950 border border-blue-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs leading-relaxed"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        Elrendezés
                      </label>
                      <select
                        value={headerLayout}
                        onChange={(e) => setHeaderLayout(e.target.value as any)}
                        className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-950 border border-blue-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                      >
                        <option value="contact_left">Elérhetőség bal | Szervnév jobb</option>
                        <option value="name_left">Szervnév bal | Elérhetőség jobb</option>
                        <option value="center">Középre zárt elrendezés</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[10px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                          Lábléc Bal
                        </label>
                        <input
                          type="text"
                          value={footerLeft}
                          onChange={(e) => setFooterLeft(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-blue-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                          Lábléc Közép
                        </label>
                        <input
                          type="text"
                          value={footerCenter}
                          onChange={(e) => setFooterCenter(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-blue-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-mono font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                          Lábléc Jobb
                        </label>
                        <input
                          type="text"
                          value={footerRight}
                          onChange={(e) => setFooterRight(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-blue-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                        />
                      </div>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                      >
                        {saving ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span>Szervezeti Fejléc Mentése</span>
                      </button>
                    </div>
                  </div>

                  {/* JOBB OLDAL: ELŐNÉZET */}
                  <div className="lg:col-span-5 bg-white dark:bg-slate-950 rounded-2xl border border-blue-200/80 dark:border-slate-800 p-5 space-y-4">
                    <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">
                      [ Szervezeti Dokumentum / Export Előnézet ]
                    </span>
                    <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                      <div className="font-bold text-slate-900 dark:text-white mb-2">{name}</div>
                      <div className="text-[11px] text-slate-600 dark:text-slate-400 whitespace-pre-line mb-3">
                        {contactsText}
                      </div>
                      <div className="border-t border-slate-200 dark:border-slate-800 pt-2 flex justify-between text-[10px] text-slate-400 font-mono">
                        <span>{footerLeft}</span>
                        <span>{footerCenter}</span>
                        <span>{footerRight}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </form>

              {/* ── KÖZZÉTÉTELI MÓD & EXPORT PANEL (Közadat.hu CSV, Statikus, Mappa-sablon) ── */}
              <div className="mt-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                      <Globe className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">📢 Közzétételi Mód & Export Eszközök</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Válassza ki a közzététel típusát, és töltse le a jogszabályilag előírt exportcsomagokat
                      </p>
                    </div>
                  </div>
                  {publishModeSaved && (
                    <span className="text-xs text-emerald-600 font-semibold animate-fade-in">
                      ✅ Közzétételi mód elmentve!
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                  {/* Lokális */}
                  <label
                    onClick={() => setPublishMode('local')}
                    className={`p-4 rounded-xl border cursor-pointer transition flex flex-col justify-between ${
                      publishMode === 'local'
                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20 shadow-xs'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <input
                          type="radio"
                          name="publish_mode"
                          checked={publishMode === 'local'}
                          onChange={() => setPublishMode('local')}
                          className="text-indigo-600 focus:ring-indigo-500"
                        />
                        <strong className="text-sm text-slate-900 dark:text-white">Lokális</strong>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Nincs adattovábbítás a felhőbe. Csak letölthető statikus export – a Hivatal a saját szerverén teszi közzé.
                      </p>
                    </div>
                  </label>

                  {/* Hibrid */}
                  <label
                    onClick={() => setPublishMode('hybrid')}
                    className={`p-4 rounded-xl border cursor-pointer transition flex flex-col justify-between ${
                      publishMode === 'hybrid'
                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-xs'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <input
                          type="radio"
                          name="publish_mode"
                          checked={publishMode === 'hybrid'}
                          onChange={() => setPublishMode('hybrid')}
                          className="text-emerald-600 focus:ring-emerald-500"
                        />
                        <strong className="text-sm text-slate-900 dark:text-white">Hibrid</strong>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        A kezelés helyben történik, de a közzétettnek jelölt oldalak egy Cloudflare-en hosztolt publikus oldalon is megjelennek.
                      </p>
                    </div>
                  </label>

                  {/* Felhő alapú */}
                  <label
                    onClick={() => setPublishMode('cloud')}
                    className={`p-4 rounded-xl border cursor-pointer transition flex flex-col justify-between ${
                      publishMode === 'cloud'
                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <input
                          type="radio"
                          name="publish_mode"
                          checked={publishMode === 'cloud'}
                          onChange={() => setPublishMode('cloud')}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <strong className="text-sm text-slate-900 dark:text-white">Felhő alapú</strong>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Minden a felhőben – a rendszer üzemeltetője (fő szervezet) mindenhez hozzáfér és azonnal publikálható.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Gombok */}
                <div className="pt-2 flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={savePublishMode}
                    disabled={savingPublishMode}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-xs transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" />
                    <span>{savingPublishMode ? 'Mentés...' : 'Mód mentése'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={downloadStaticSite}
                    className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                    title="Önálló HTML+CSS közzétételi csomag letöltése"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Statikus export letöltése (ZIP)</span>
                  </button>

                  <button
                    type="button"
                    onClick={downloadFolderTemplate}
                    className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                    title="Mappánként/pontonként bontott ZIP a jelenlegi tartalommal"
                  >
                    <Folder className="w-3.5 h-3.5" />
                    <span>Mappa-sablon letöltése (ZIP)</span>
                  </button>

                  <button
                    type="button"
                    onClick={downloadKozadatCsv}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                    title="A közadat.hu portál kötelező import-formátumának megfelelő CSV (Windows-1250, ';'-tal tagolva)"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                    <span>közadat.hu CSV export</span>
                  </button>
                </div>

                {/* Publikus URL Doboz */}
                <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <strong className="text-slate-700 dark:text-slate-300 shrink-0">Publikus URL:</strong>
                    <a
                      href={`${window.location.origin}/public.html?t=${encodeURIComponent(tenant?.slug || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-cyan-400 hover:underline truncate"
                    >
                      {`${window.location.origin}/public.html?t=${encodeURIComponent(tenant?.slug || '')}`}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/public.html?t=${encodeURIComponent(tenant?.slug || '')}`);
                      setCopiedUrl(true);
                      setTimeout(() => setCopiedUrl(false), 2500);
                    }}
                    className="px-2.5 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-50 transition flex items-center gap-1 shrink-0 cursor-pointer"
                  >
                    <Copy className="w-3 h-3" />
                    <span>{copiedUrl ? 'Másolva!' : 'Másolás'}</span>
                  </button>
                </div>

                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  A publikus oldalon és a statikus exportban csak azok az oldalak jelennek meg, amiket az oldal szerkesztőjében külön <strong>"Közzétéve"</strong>-ként megjelöltél.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
